import type { QueueItem as QueueItemType } from '../types'
import { formatBytes } from '../lib/fileValidation'
import { errorMessageFor } from '../lib/errorMessages'

interface QueueItemProps {
  item: QueueItemType
  onRemove: (id: string) => void
  onDownload: (id: string) => void
  onRetry: (id: string) => void
}

const STATUS_LABELS: Record<QueueItemType['status'], string> = {
  waiting: 'Waiting',
  reading: 'Reading…',
  converting: 'Converting',
  succeeded: 'Succeeded',
  failed: 'Failed',
  cancelled: 'Cancelled',
}

export default function QueueItem({ item, onRemove, onDownload, onRetry }: QueueItemProps) {
  const { id, displayName, size, status, progressPhase, errorCode } = item
  const isTerminal = status === 'succeeded' || status === 'failed' || status === 'cancelled'
  const isActive = status === 'reading' || status === 'converting'

  return (
    <li className="row" aria-label={`${displayName} ${STATUS_LABELS[status]}`} role="listitem">
      <span className="name" title={displayName}>
        {displayName}
      </span>
      <span className="size">{formatBytes(size)}</span>
      <span className={`status status-${status}`} aria-live="off">
        {isActive && progressPhase ? `${STATUS_LABELS[status]} · ${progressPhase}` : STATUS_LABELS[status]}
      </span>
      <span className="actions">
        {status === 'succeeded' && (
          <button
            type="button"
            className="button button-small"
            onClick={() => onDownload(id)}
            aria-label={`Download ${displayName}`}
          >
            Download
          </button>
        )}
        {status === 'failed' && (
          <button
            type="button"
            className="button button-small retry"
            onClick={() => onRetry(id)}
            aria-label={`Retry ${displayName}`}
          >
            Retry
          </button>
        )}
        <button
          type="button"
          className="remove"
          onClick={() => onRemove(id)}
          disabled={false}
          aria-label={isActive ? `Cancel ${displayName}` : `Remove ${displayName}`}
          title={isActive ? 'Cancel' : 'Remove'}
        >
          {isActive ? 'Cancel' : '×'}
        </button>
      </span>
      {status === 'failed' && errorCode && (
        <div className="error-detail" role="alert">
          {errorMessageFor(errorCode)}
        </div>
      )}
      {isTerminal && !item.result && status !== 'failed' && (
        <span className="sr-only">{displayName} finished</span>
      )}
    </li>
  )
}
